import { useQuery } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { ScrollArea } from '../ui/ScrollArea';
import { useTranslation } from '../../../lib/i18n';
import { logPageClickBi } from '../../../lib/bi-logger';
import { CHAT_HOME_BI } from './message-utils';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { connectorLogoUrl, fetchConnectors, type ConnectorStatus, type ConnectorsStatus } from '../../../lib/connectors';
import { resolveSuggestedConnect } from '../../../lib/suggested-task-connect';
import { useConnectConnector } from '../../../lib/use-connect-connector';
import { useSuggestedTasks, type SuggestedTask } from '../../../lib/suggested-tasks';

// The connect chip resolves the toolkit against, and re-syncs, the SHARED connectors query cache — so
// the tap can resolve a `ConnectorStatus` synchronously (keeping the user gesture for the OAuth popup)
// and a connect done from the chip is visible to every other reader of the list. Both closures are
// module-scoped (they close over the query singleton, nothing per-render) so the connect hook's inputs
// are stable.
const emptyChatConnectors = () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.connectors ?? [];
const refreshEmptyChatConnectors = () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors });

/** How the row lays the chips out — a decision the caller makes from the pane's width. */
export type SuggestedTaskChipsLayout = 'wrap' | 'scroll';

/**
 * The suggested-task chips on the empty chat — the genome's answer to "what could I ask for?",
 * personalized per business and refreshed on every genome recalc.
 *
 * <p>The empty chat has two Figma layouts, and this row renders in both — the caller picks `layout`
 * from how much room the pane has:
 *
 * - **`wrap`** (Figma 17208:22003 — the chat fully open, or a docked pane wide enough): the chips are
 *   centred wrapped rows sitting directly under the hero.
 * - **`scroll`** (Figma 17225:28520 — the ~370px side dock / a narrow viewport): the chips are a single
 *   horizontal lane pinned just above the composer, scrolling off the pane's edge rather than wrapping.
 *
 * Both layouts draw the SAME chip — a round pill (`Symphony Button Round`), single line.
 *
 * `scroll` is the layout BOTH phones render, unconditionally — the lane above the composer is the
 * shared presentation, not a web idea. Only web needs the choice at all, because only web's chat has
 * more than one width (a ~370px dock, a floating panel, a fullscreen column); a phone chat is always
 * the full screen, so it is never asked the question `useContainerNarrow` answers. That is a capability
 * exception, not a gap — see `.claude/rules/orion-web-chat.md`.
 *
 * <p>Three rules the design turns on:
 *
 * 1. **A tap on a READY chip PREFILLS, it does not send** — and hovering first SHOWS what would be
 *    sent. These are the assistant's guesses, and a guess that acts on its own is one the user cannot
 *    decline. The reveal is the preview; the composer keeps the last word.
 * 2. **The row is silent when it has nothing.** Loading, failed, or none authored all render null.
 *    The composer below is the feature; a spinner or an error where six optional buttons would be is
 *    strictly worse than the buttons never appearing.
 * 3. **A connect chip says so on its face, and a tap on it STARTS the connection.** At most one chip
 *    names an app the business has not connected; it carries a visible marker, not just a tooltip, AND
 *    its tap runs the real connect flow in place — the same composio/native-calendar/assistant-chat rail
 *    the Connectors page dispatches (`useConnectConnector`), resolved from the prefetched connectors list
 *    (`resolveSuggestedConnect`). It falls back to navigating to that connector's Connectors card when the
 *    slug can't be resolved yet, and to the ordinary prefill when the task names no connector at all —
 *    never a prefilled prompt for an unconnected app, which would only bounce the ask back one turn later.
 *    See `lib/suggested-task-connect.ts`.
 *
 * **Written-down deviation — the chip is a round, filled pill.** The Figma frames draw it as the
 * `Symphony Button Round`, skin Light: a fully-rounded 42px pill with a white fill. orion-web's `Button`
 * has neither a round shape nor a light skin, and CLAUDE.md's "Buttons" section says the app design
 * carries no pills — so the chip stays the DS `<Button variant="outlined" size="large">` (a real DS
 * button — `button-spec` is satisfied) with two overrides in `PILL`: the radius, and the frame's white
 * fill via the theme-aware `--ds-surface-sunken` (white in light, the dark-elevated surface in dark, so
 * it is not a hardcoded colour).
 *
 * The deviation is WEB'S ALONE because only web has a DS `Button` standing between it and the frame:
 * both phones draw the pill directly (iOS a `Capsule` filled `backgroundElevated`, Android a
 * `RoundedCornerShape(999.dp)` on the same), and both are deliberately BORDERLESS — a connect chip's
 * "not ready" cue is its inner Connect button, never an outer outline. Web keeps the outlined skin's
 * hairline only because the chat surface here is itself white and the pill would otherwise vanish into
 * it. Same chip, one surface paying a DS tax for it.
 *
 * **Written-down parity exception — the reveal is hover-only, so touch web does not get it.** iOS and
 * Android long-press for the same text; on the web the reveal is the native `title` tooltip, which has
 * no touch equivalent. A mobile-web user can still read the prompt — it lands in the composer, above
 * the keyboard, before they send — so the information is not lost, only its preview. Fixing it properly
 * means a bespoke press-and-hold popover, which is more machinery than the gap justifies today; revisit
 * if mobile web becomes a primary chat surface. (Repo golden rule: an exception is written down, never
 * a silent gap.)
 */
export function SuggestedTaskChips({
  enabled,
  layout,
  onPick,
  onConnected,
}: {
  /** Only fetch when the empty landing is actually showing — no chips, no request. */
  enabled: boolean;
  /** `wrap` = centred rows under the hero; `scroll` = one horizontal lane above the composer. */
  layout: SuggestedTaskChipsLayout;
  /**
   * The chip was tapped. The caller puts `task.prompt` in the composer and focuses it; it must NOT send.
   *
   * The whole task travels, not just its prompt: the caller also parks `task.label` so that IF the user
   * goes on to send, the chip can be recorded as acted on and stop coming back. The label is what
   * identifies the chip to the server — see `lib/suggested-task-ack.ts`. Same shape iOS and Android
   * settled on for the same reason.
   */
  onPick: (task: SuggestedTask) => void;
  /** Arm the assistant-wake after a chip's connect lands, with the connector's display name (the chat
   *  surface's `noteConnected`, which drives the "Inspecting your {name}." handoff placeholder). */
  onConnected?: (name: string) => void;
}) {
  const { t } = useTranslation();
  const state = useSuggestedTasks(enabled);
  // Prefetch the connectors list the moment the empty landing shows, so a CONNECT chip can resolve its
  // toolkit synchronously on tap (a resolve behind an await would lose the popup gesture). Reuses the
  // shared `queryKeys.connectors` fetch — the same list the in-chat connector card and the Connectors
  // page read — so nothing is fetched twice.
  const connectorsQuery = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, enabled });
  const { connect } = useConnectConnector({ getConnectors: emptyChatConnectors, refresh: refreshEmptyChatConnectors, onConnected });
  if (state.status !== 'ready') return null;

  const chips = state.tasks.map((task, i) => (
    <Chip
      key={`${task.label}-${i}`}
      task={task}
      connectLabel={t('common.connect')}
      layout={layout}
      onPick={onPick}
      connect={connect}
      connectors={connectorsQuery.data?.connectors}
    />
  ));

  if (layout === 'scroll') {
    // The lane scrolls sideways off the pane's edge (Figma draws it 563px wide inside a 370px dock),
    // so it needs a real overflow container, not a `flex-wrap`. `bar="hidden"` because the affordance
    // is the next chip peeking in, the same as the mobile card strips.
    return (
      <ScrollArea axis="horizontal" bar="hidden" style={SCROLL_WRAP}>
        <div style={SCROLL_ROW}>{chips}</div>
      </ScrollArea>
    );
  }

  return <div style={WRAP}>{chips}</div>;
}

function hideLogoOnError(e: React.SyntheticEvent<HTMLImageElement>) {
  // A logo CDN that 404s a slug must cost the chip its picture, not its text.
  (e.currentTarget as HTMLImageElement).style.display = 'none';
}

/**
 * The chip's `<Button>`, in whichever of the two shapes ButtonProps actually offers — a link when
 * `connectHref` is set, a plain button otherwise. Split out as its own component (rather than a shared
 * props object spread into one `<Button>` call) because that object's type is a union of the two
 * shapes, and TS cannot narrow a *spread* of a union the way it narrows a value — see
 * https://github.com/microsoft/TypeScript/issues/28486. `commonProps` below is NOT itself a union
 * (both call sites build it from the same literal shape), so each of the two returns individually type-checks
 * clean against the one ButtonProps branch it uses.
 */
function ChipButton({
  connectHref,
  title,
  ariaLabel,
  onClick,
  style,
  startIcon,
  endIcon,
  children,
}: {
  connectHref: string | null;
  title: string;
  ariaLabel: string;
  onClick: () => void;
  style: React.CSSProperties;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children: React.ReactNode;
}) {
  // `size="large"` is the frame's 42px chip; PILL overrides only the radius to make it round.
  const commonProps = { variant: 'outlined' as const, size: 'large' as const, title, 'aria-label': ariaLabel, style: { ...PILL, ...style }, startIcon, endIcon };
  if (connectHref) {
    return (
      <Button {...commonProps} href={connectHref} onClick={onClick}>
        {children}
      </Button>
    );
  }
  return (
    <Button {...commonProps} onClick={onClick}>
      {children}
    </Button>
  );
}

function Chip({
  task,
  connectLabel,
  layout,
  onPick,
  connect,
  connectors,
}: {
  task: SuggestedTask;
  connectLabel: string;
  layout: SuggestedTaskChipsLayout;
  onPick: (task: SuggestedTask) => void;
  connect: (connector: ConnectorStatus) => void;
  connectors: ConnectorStatus[] | undefined;
}) {
  // The reveal shows the PROMPT — the exact text a tap puts in the composer — so the user can read
  // what they are about to send before they send it. (`why` is the internal rationale; it rides the
  // accessibility label below, where length is free, rather than competing for the tooltip.) A connect
  // chip is prefixed with its state, the more urgent thing to know before tapping.
  const tooltip = task.requiresConnect ? `${connectLabel} · ${task.prompt}` : task.prompt;
  // A `requiresConnect` chip's tap STARTS the connection instead of prefilling a prompt that would only
  // bounce back asking for the same connection one turn later. When the toolkit resolves to a live
  // connector row we run the real connect flow in place (`connect`, no href → a real button); when it
  // doesn't (list not loaded, or an unknown slug) we fall back to navigating to its Connectors card
  // (`connectHref` → an `<a>`); and a task that names no connector at all degrades to the ordinary
  // prefill, same as a chip that was never marked `requiresConnect`. See `resolveSuggestedConnect`.
  const action = task.requiresConnect ? resolveSuggestedConnect(task.connector, connectors) : { kind: 'prefill' as const };
  const connectHref = action.kind === 'navigate' ? action.href : null;
  const ariaLabel = task.why ? `${task.label} — ${task.why}` : task.label;
  const onClick =
    action.kind === 'connect'
      ? () => {
          logPageClickBi({ ...CHAT_HOME_BI, button_name: 'suggested_task_connect', location: 'composer' });
          connect(action.connector);
        }
      : action.kind === 'navigate'
        ? () => {
            // The `<a href>` performs the navigation; this only records the tap.
            logPageClickBi({ ...CHAT_HOME_BI, button_name: 'suggested_task_connect', location: 'composer' });
          }
        : () => {
            // Which chip was picked, not merely that one was — parity with iOS/Android, which both log this.
            // Without it, any read on which suggestions earn their place is blind to desktop.
            logPageClickBi({ ...CHAT_HOME_BI, button_name: 'suggested_task_selected', location: 'composer' });
            onPick(task);
          };

  // In the horizontal lane the pill is its natural width and never shrinks — it just scrolls into view.
  // In the wrapped rows it may shrink to fit the row, and a very long label ellipsises rather than
  // stretching the row toward its full width.
  const scroll = layout === 'scroll';
  return (
    <ChipButton
      connectHref={connectHref}
      title={tooltip}
      ariaLabel={ariaLabel}
      onClick={onClick}
      style={{ flexShrink: scroll ? 0 : 1, maxWidth: scroll ? 'none' : '100%' }}
      startIcon={
        task.connector ? (
          <img src={connectorLogoUrl(task.connector)} alt="" aria-hidden width={16} height={16} style={ICON} onError={hideLogoOnError} />
        ) : undefined
      }
      endIcon={task.requiresConnect ? <span style={CONNECT_MARK}>{connectLabel}</span> : undefined}
    >
      <span style={scroll ? undefined : LABEL}>{task.label}</span>
    </ChipButton>
  );
}

// The round, FILLED, BORDERLESS pill the frame draws (`Symphony Button Round`, skin Light). The DS
// Button has no round/light skin, so the overrides live here: a pill radius, the frame's white fill via
// the theme-aware surface token (white in light, the dark-elevated surface in dark), and a TRANSPARENT
// border. Borderless is faithful to the frame and reads because the pill sits on the chat's own
// `#f3f3f3` (light) / dark surface, which the white/elevated fill lifts off of on its own. The 1px the
// outlined skin reserves is kept (as transparent) so the box never shifts. See the header doc deviation.
const PILL: React.CSSProperties = {
  borderRadius: 9999,
  background: 'var(--ds-surface-sunken)',
  borderColor: 'transparent',
};

// Enough room (Figma 17208:22003): centred wrapped rows under the hero.
const WRAP: React.CSSProperties = {
  width: '100%',
  maxWidth: 760,
  alignSelf: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 12,
  padding: '0 24px 4px',
};

// Tight pane (Figma 17225:28520): one horizontal lane above the composer.
const SCROLL_WRAP: React.CSSProperties = { width: '100%', boxSizing: 'border-box' };
const SCROLL_ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 12,
  // `max-content` so the row keeps its full width and the ScrollArea scrolls it, rather than the flex
  // items being squeezed to fit the pane. Bottom padding keeps the pills clear of the scroll edge.
  width: 'max-content',
  padding: '2px 16px 4px',
};

const ICON: React.CSSProperties = {
  width: 16,
  height: 16,
  borderRadius: 3,
  flexShrink: 0,
};

const LABEL: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// The inner "Connect" marker — the frame's small bordered pill riding inside the chip.
// The inline "Connect" button the frame draws (17202:21220): a real bordered pill, not a quiet marker —
// white fill, the light `#dbdbdb` border, 14/18 SemiBold in PRIMARY ink, 26px tall. It must read as a
// tappable button (the whole chip connects), never as a disabled hint.
const CONNECT_MARK: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  height: 26,
  padding: '0 12px',
  fontSize: 14,
  lineHeight: '18px',
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'var(--ds-text-primary)',
  background: 'var(--ds-surface-sunken)',
  border: '1px solid var(--ds-border-light-default)',
  borderRadius: 999,
};
